import { confirmDialog } from '../shared/rc-confirm-dialog';
import { documentsApi, dossiersApi, rentalAgreementsApi, rentalPaymentLogsApi, tenantsApi, type CreateDocumentRequest, type CreateTenantRequest, type DocumentDto, type ExportTenantDossierRequest, type PropertyDto, type RentalAgreementDto, type RentalPaymentMonthDto, type SaveRentalPaymentLogRequest, type TenantDto, type UpdateRentalAgreementRequest, type UpdateTenantRequest } from '../../services/api';
import { ApiError } from '../../services/core.api';
import { t as translate } from '../../services/i18n';
import { toastService } from '../../services/toast';
import { DOC_TYPES, escapeHtml, formatAgreementPeriod, formatDate, formatMonthTitle, getDocumentTypeLabel, hasCurrentAgreement, TENANT_ICONS } from './tenant-shared';

function formatMonthlyRent(amount?: number): string {
  return amount != null ? `&euro;${amount.toFixed(2)}${translate('property.perMonth')}` : '-';
}

function getEditorValue(root: ParentNode, name: string): string | undefined {
  const editor = root.querySelector(`rc-rich-text-editor[name="${name}"]`) as HTMLElement & { value?: string } | null;
  const value = editor?.value?.trim() || '';
  return value || undefined;
}

function appendModalOverlay(content: string, zIndex?: string): HTMLDivElement {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  if (zIndex) {
    overlay.style.zIndex = zIndex;
  }
  overlay.innerHTML = content;
  document.body.appendChild(overlay);
  return overlay;
}

function clearDialogError(root: ParentNode): void {
  const errorBox = root.querySelector<HTMLElement>('[data-form-error]');
  if (!errorBox) {
    return;
  }

  errorBox.hidden = true;
  errorBox.textContent = '';
}

function showDialogError(root: ParentNode, message: string): void {
  const errorBox = root.querySelector<HTMLElement>('[data-form-error]');
  if (!errorBox) {
    return;
  }

  errorBox.hidden = false;
  errorBox.textContent = message;
}

export function openPaymentLogModal(options: {
  agreement: RentalAgreementDto;
  year: number;
  month: number;
  entry?: RentalPaymentMonthDto;
  onSaved: () => Promise<void>;
}): void {
  const { agreement, year, month, entry, onSaved } = options;
  const overlay = appendModalOverlay(`
    <div class="modal">
      <div class="modal-header">
        <div>
          <h2>${translate('tenantPayments.modalTitle')}</h2>
          <p class="modal-subtitle">${escapeHtml(agreement.propertyAddress)} · ${escapeHtml(formatMonthTitle(year, month))}</p>
        </div>
        <button class="modal-close" data-action="cancel">
          ${TENANT_ICONS.close}
        </button>
      </div>
      <form>
        <div class="form-error" data-form-error hidden></div>
        <div class="form-group">
          <label>${translate('tenantPayments.statusLabel')}</label>
          <select name="status">
            <option value="">${translate('tenantPayments.clearStatus')}</option>
            <option value="Paid" ${entry?.status === 'Paid' ? 'selected' : ''}>${translate('tenantPayments.status.paid')}</option>
            <option value="Unpaid" ${entry?.status === 'Unpaid' ? 'selected' : ''}>${translate('tenantPayments.status.unpaid')}</option>
          </select>
        </div>
        <div class="form-group" data-paid-on-group ${entry?.status === 'Paid' ? '' : 'hidden'}>
          <label>${translate('tenantPayments.paidOn')}</label>
          <input name="paidOn" type="date" value="${entry?.status === 'Paid' ? (entry?.paidOn ?? '') : ''}" ${entry?.status === 'Paid' ? '' : 'disabled'}>
        </div>
        <div class="form-group">
          <label>${translate('tenantPayments.note')}</label>
          <textarea name="note" rows="4" placeholder="${escapeHtml(translate('tenantPayments.notePlaceholder'))}">${escapeHtml(entry?.note ?? '')}</textarea>
        </div>
        <div class="modal-actions">
          <button type="button" class="btn btn-outline" data-action="cancel">${translate('common.cancel')}</button>
          <button type="submit" class="btn btn-primary">${translate('common.save')}</button>
        </div>
      </form>
    </div>
  `, '200');

  const close = () => overlay.remove();
  overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
  overlay.querySelectorAll('[data-action="cancel"]').forEach((button) => button.addEventListener('click', close));

  const statusSelect = overlay.querySelector('select[name="status"]') as HTMLSelectElement;
  const paidOnInput = overlay.querySelector('input[name="paidOn"]') as HTMLInputElement;
  const paidOnGroup = overlay.querySelector<HTMLElement>('[data-paid-on-group]');
  const errorBox = overlay.querySelector<HTMLElement>('[data-form-error]');
  const updatePaidOnVisibility = () => {
    const isPaid = statusSelect.value === 'Paid';
    if (paidOnGroup) {
      paidOnGroup.hidden = !isPaid;
    }
    paidOnInput.disabled = !isPaid;
    if (!isPaid) {
      paidOnInput.value = '';
    }
  };

  updatePaidOnVisibility();
  statusSelect.addEventListener('change', updatePaidOnVisibility);

  overlay.querySelector('form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const submitButton = overlay.querySelector('[type="submit"]') as HTMLButtonElement;
    submitButton.disabled = true;
    submitButton.textContent = translate('timeline.saving');
    if (errorBox) {
      errorBox.hidden = true;
      errorBox.textContent = '';
    }

    const formData = new FormData(event.target as HTMLFormElement);
    const status = (formData.get('status') as string) || undefined;
    const request: SaveRentalPaymentLogRequest = {
      year,
      month,
      status,
      paidOn: status === 'Paid' ? ((formData.get('paidOn') as string) || undefined) : undefined,
      note: ((formData.get('note') as string) || '').trim() || undefined,
    };

    try {
      await rentalPaymentLogsApi.save(agreement.id, request);
      close();
      await onSaved();
    } catch (error) {
      console.error('Failed to save rental payment log:', error);
      if (errorBox) {
        errorBox.textContent = error instanceof Error && error.message
          ? error.message
          : translate('tenantPayments.saveError');
        errorBox.hidden = false;
      }
      submitButton.disabled = false;
      submitButton.textContent = translate('common.save');
    }
  });
}

export function openTenantForm(options: {
  tenant?: TenantDto;
  properties: PropertyDto[];
  allAgreementsByTenant: Map<string, RentalAgreementDto[]>;
  onSaved: () => Promise<void>;
}): void {
  const { tenant, properties, allAgreementsByTenant, onSaved } = options;
  const tenantAgreements = tenant ? (allAgreementsByTenant.get(tenant.id) ?? []) : [];
  const agreement = tenantAgreements.find((item) => hasCurrentAgreement([item])) ?? tenantAgreements[0] ?? null;

  const overlay = appendModalOverlay(`
    <div class="modal">
      <div class="modal-header">
        <h2>${tenant ? translate('tenant.form.editTitle') : translate('tenant.form.addTitle')}</h2>
        <button class="modal-close" data-action="cancel">
          ${TENANT_ICONS.close}
        </button>
      </div>
      <form>
        <div class="form-error" data-form-error hidden></div>
        ${!tenant ? `
        <div class="form-group">
          <label>${translate('common.property')} *</label>
          <select name="propertyId" required>
            ${properties.map((property) => `<option value="${property.id}" ${agreement?.propertyId === property.id ? 'selected' : ''}>${escapeHtml(property.displayName || property.address)}</option>`).join('')}
          </select>
        </div>` : ''}
        <div class="form-row">
          <div class="form-group">
            <label>${translate('common.name')} *</label>
            <input name="firstName" required value="${escapeHtml(tenant?.firstName || '')}">
          </div>
          <div class="form-group">
            <label>${translate('tenant.form.lastName')} *</label>
            <input name="lastName" required value="${escapeHtml(tenant?.lastName || '')}">
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label>${translate('common.email')}</label>
            <input name="email" type="email" value="${escapeHtml(tenant?.email || '')}">
          </div>
          <div class="form-group">
            <label>${translate('common.phone')}</label>
            <input name="phone" value="${escapeHtml(tenant?.phone || '')}">
          </div>
        </div>
        <div class="form-group">
          <label>${translate('common.notes')}</label>
          <rc-rich-text-editor
            name="notes"
            placeholder="${escapeHtml(translate('common.notesPlaceholder'))}"
            value="${escapeHtml(tenant?.notes || '')}"></rc-rich-text-editor>
        </div>
        ${!tenant ? `
        <div class="form-group">
          <label>${translate('common.rentAmountPerMonth')} *</label>
          <input name="rentAmount" type="number" step="0.01" min="0" required value="${agreement?.rentAmount ?? ''}">
        </div>
        <div class="form-row">
          <div class="form-group">
            <label>${translate('common.startDate')} *</label>
            <input name="startDate" type="date" required value="${agreement?.startDate || ''}">
          </div>
          <div class="form-group">
            <label>${translate('common.endDate')}</label>
            <input name="endDate" type="date" value="${agreement?.endDate || ''}">
          </div>
        </div>` : ''}
        <div class="modal-actions">
          <button type="button" class="btn btn-outline" data-action="cancel">${translate('common.cancel')}</button>
          <button type="submit" class="btn btn-primary">${tenant ? translate('common.save') : translate('common.add')}</button>
        </div>
      </form>
    </div>
  `);

  const close = () => overlay.remove();
  overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
  overlay.querySelectorAll('[data-action="cancel"]').forEach((button) => button.addEventListener('click', close));

  overlay.querySelector('form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const submitBtn = overlay.querySelector('[type="submit"]') as HTMLButtonElement;
    clearDialogError(overlay);
    submitBtn.disabled = true;
    const formData = new FormData(event.target as HTMLFormElement);

    try {
      if (tenant) {
        const data: UpdateTenantRequest = {
          firstName: formData.get('firstName') as string,
          lastName: formData.get('lastName') as string,
          email: (formData.get('email') as string) || undefined,
          phone: (formData.get('phone') as string) || undefined,
          notes: getEditorValue(overlay, 'notes'),
        };
        await tenantsApi.update(tenant.id, data);
      } else {
        const data: CreateTenantRequest = {
          propertyId: formData.get('propertyId') as string,
          firstName: formData.get('firstName') as string,
          lastName: formData.get('lastName') as string,
          email: (formData.get('email') as string) || undefined,
          phone: (formData.get('phone') as string) || undefined,
          notes: getEditorValue(overlay, 'notes'),
          rentAmount: parseFloat(formData.get('rentAmount') as string),
          startDate: formData.get('startDate') as string,
          endDate: (formData.get('endDate') as string) || undefined,
        };
        const createdTenant = await tenantsApi.create(data);
        await rentalAgreementsApi.create({
          tenantId: createdTenant.id,
          propertyId: data.propertyId,
          rentAmount: data.rentAmount,
          startDate: data.startDate,
          endDate: data.endDate,
        });
      }
      close();
      await onSaved();
    } catch (error) {
      console.error('Failed to save tenant:', error);
      showDialogError(overlay, error instanceof ApiError ? error.message : translate('common.errorGeneric'));
      submitBtn.disabled = false;
    }
  });
}

export function openEditAgreementModal(options: {
  tenant: TenantDto;
  agreement: RentalAgreementDto;
  onSaved: () => Promise<void>;
}): void {
  const { tenant, agreement, onSaved } = options;
  const overlay = appendModalOverlay(`
    <div class="modal">
      <div class="modal-header">
        <h2>${translate('agreement.editTitle')}</h2>
        <button class="modal-close" data-action="cancel">
          ${TENANT_ICONS.close}
        </button>
      </div>
      <form>
        <div class="form-error" data-form-error hidden></div>
        <div class="form-row">
          <div class="form-group">
            <label>${translate('common.property')}</label>
            <input class="form-input-static" type="text" value="${escapeHtml(agreement.propertyAddress)}" disabled>
          </div>
          <div class="form-group">
            <label>${translate('common.tenant')}</label>
            <input class="form-input-static" type="text" value="${escapeHtml(`${tenant.firstName} ${tenant.lastName}`)}" disabled>
          </div>
        </div>
        <div class="form-group">
          <label>${translate('common.rentAmountPerMonth')} *</label>
          <input name="rentAmount" type="number" step="0.01" min="0" required value="${agreement.rentAmount ?? ''}">
        </div>
        <div class="form-row">
          <div class="form-group">
            <label>${translate('common.startDate')} *</label>
            <input name="startDate" type="date" required value="${agreement.startDate || ''}">
          </div>
          <div class="form-group">
            <label>${translate('common.endDate')}</label>
            <input name="endDate" type="date" value="${agreement.endDate || ''}">
          </div>
        </div>
        <div class="modal-actions">
          <button type="button" class="btn btn-outline" data-action="cancel">${translate('common.cancel')}</button>
          <button type="submit" class="btn btn-primary">${translate('common.save')}</button>
        </div>
      </form>
    </div>
  `, '200');

  const close = () => overlay.remove();
  overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
  overlay.querySelectorAll('[data-action="cancel"]').forEach((button) => button.addEventListener('click', close));

  overlay.querySelector('form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const submitBtn = overlay.querySelector('[type="submit"]') as HTMLButtonElement;
    clearDialogError(overlay);
    submitBtn.disabled = true;

    const formData = new FormData(event.target as HTMLFormElement);
    const data: UpdateRentalAgreementRequest = {
      tenantId: tenant.id,
      propertyId: agreement.propertyId,
      rentAmount: parseFloat(formData.get('rentAmount') as string),
      startDate: formData.get('startDate') as string,
      endDate: (formData.get('endDate') as string) || undefined,
    };

    try {
      await rentalAgreementsApi.update(agreement.id, data);
      close();
      await onSaved();
    } catch (error) {
      console.error('Failed to update agreement:', error);
      showDialogError(overlay, error instanceof ApiError ? error.message : translate('agreement.updateError'));
      submitBtn.disabled = false;
    }
  });
}

export function openRentChangeWizard(options: {
  tenant: TenantDto;
  agreement: RentalAgreementDto;
  onSaved: () => Promise<void>;
}): void {
  const { tenant, agreement, onSaved } = options;
  let step = 1;
  let stopDate = '';
  let newStartDate = '';
  let newRentAmount = '';
  let wizardError = '';

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.style.zIndex = '200';

  const modal = document.createElement('div');
  modal.className = 'modal';

  const dot = (n: number) => {
    const done = step > n;
    const active = step >= n;
    const bg = active ? 'var(--primary,#6366f1)' : 'var(--border,#e2e8f0)';
    const color = active ? 'white' : 'var(--text-muted,#94a3b8)';
    return `<div style="width:2rem;height:2rem;border-radius:50%;background:${bg};color:${color};display:flex;align-items:center;justify-content:center;font-weight:600;font-size:0.875rem;flex-shrink:0;">${done ? '✓' : n}</div>`;
  };

  const connector = (filled: boolean) =>
    `<div style="flex:1;height:2px;background:${filled ? 'var(--primary,#6366f1)' : 'var(--border,#e2e8f0)'};margin-top:1rem;"></div>`;

  const stepLabel = (n: number, label: string) => `
    <div style="display:flex;flex-direction:column;align-items:center;gap:0.375rem;">
      ${dot(n)}
      <span style="font-size:0.7rem;color:${step >= n ? 'var(--text,#0f172a)' : 'var(--text-muted,#94a3b8)'};text-align:center;line-height:1.3;white-space:nowrap;">${label}</span>
    </div>`;

  const renderWizard = () => {
    const suggestedStart = stopDate
      ? (() => {
          const date = new Date(stopDate);
          date.setDate(date.getDate() + 1);
          return date.toISOString().slice(0, 10);
        })()
      : '';

    const infoBox = (rows: [string, string][]) => `
      <div style="background:var(--surface,#f8fafc);border:1px solid var(--border,#e2e8f0);border-radius:0.5rem;padding:1rem;margin-bottom:1rem;">
        <div style="display:grid;grid-template-columns:auto 1fr;gap:0.4rem 1rem;font-size:0.875rem;">
          ${rows.map(([label, value]) => `<span style="color:var(--text-muted,#64748b);">${label}</span><span>${value}</span>`).join('')}
        </div>
      </div>`;

    modal.innerHTML = `
      <div class="modal-header">
        <h2>${translate('agreement.rentChange.title')}</h2>
        <button class="modal-close" data-action="close">
          ${TENANT_ICONS.close}
        </button>
      </div>
      <div style="display:flex;align-items:flex-start;padding:1.25rem 1.5rem 0.25rem;gap:0.5rem;">
        ${stepLabel(1, translate('agreement.rentChange.currentAgreementStep'))}
        ${connector(step > 1)}
        ${stepLabel(2, translate('agreement.rentChange.newAgreementStep'))}
        ${connector(step > 2)}
        ${stepLabel(3, translate('agreement.rentChange.confirmStep'))}
      </div>
      <div style="height:1px;background:var(--border,#e2e8f0);margin:0.75rem 0;"></div>
      <div style="padding:0 1.5rem 0.25rem;">
        ${wizardError ? `<div class="form-error">${escapeHtml(wizardError)}</div>` : ''}
        ${step === 1 ? `
          <p style="color:var(--text-muted,#64748b);font-size:0.875rem;margin-bottom:1rem;">${translate('agreement.rentChange.closeCurrentHelp')}</p>
          ${infoBox([
            [translate('common.property'), escapeHtml(agreement.propertyAddress)],
            [translate('common.tenant'), escapeHtml(`${tenant.firstName} ${tenant.lastName}`)],
            [translate('common.rentAmount'), formatMonthlyRent(agreement.rentAmount)],
            [translate('common.startDate'), formatDate(agreement.startDate)],
          ])}
          <div class="form-group">
            <label>${translate('agreement.rentChange.currentEndDate')} *</label>
            <input name="stopDate" type="date" required value="${stopDate}">
          </div>
        ` : step === 2 ? `
          <p style="color:var(--text-muted,#64748b);font-size:0.875rem;margin-bottom:1rem;">${translate('agreement.rentChange.setupNewHelp')}</p>
          <div class="form-row">
            <div class="form-group">
              <label>${translate('common.property')}</label>
              <input type="text" value="${escapeHtml(agreement.propertyAddress)}" disabled style="background:var(--color-surface,#f8fafc);color:var(--color-text-muted,#64748b);">
            </div>
            <div class="form-group">
              <label>${translate('common.tenant')}</label>
              <input type="text" value="${escapeHtml(`${tenant.firstName} ${tenant.lastName}`)}" disabled style="background:var(--color-surface,#f8fafc);color:var(--color-text-muted,#64748b);">
            </div>
          </div>
          <div class="form-group">
            <label>${translate('agreement.rentChange.newStartDate')} *</label>
            <input name="newStartDate" type="date" required value="${newStartDate || suggestedStart}">
          </div>
          <div class="form-group">
            <label>${translate('agreement.rentChange.newRentAmount')} *</label>
            <input name="newRentAmount" type="number" step="0.01" min="0" required value="${newRentAmount}" placeholder="${agreement.rentAmount?.toFixed(2) ?? ''}">
          </div>
        ` : `
          <p style="color:var(--text-muted,#64748b);font-size:0.875rem;margin-bottom:1rem;">${translate('agreement.rentChange.reviewHelp')}</p>
          ${infoBox([[translate('common.property'), escapeHtml(agreement.propertyAddress)], [translate('common.tenant'), escapeHtml(`${tenant.firstName} ${tenant.lastName}`)], [translate('common.endDate'), formatDate(stopDate)]])}
          ${infoBox([
            [translate('common.property'), escapeHtml(agreement.propertyAddress)],
            [translate('common.tenant'), escapeHtml(`${tenant.firstName} ${tenant.lastName}`)],
            [translate('common.rentAmount'), formatMonthlyRent(parseFloat(newRentAmount))],
            [translate('common.startDate'), formatDate(newStartDate)],
          ])}
        `}
      </div>
      <div class="modal-actions">
        ${step > 1 ? `<button class="btn btn-outline" data-action="prev">${translate('common.previous')}</button>` : `<span></span>`}
        <div style="display:flex;gap:0.5rem;">
          <button class="btn btn-outline" data-action="close">${translate('common.cancel')}</button>
          ${step < 3
            ? `<button class="btn btn-primary" data-action="next">${translate('common.next')}</button>`
            : `<button class="btn btn-primary" data-action="confirm">${translate('common.confirm')}</button>`}
        </div>
      </div>
    `;

    modal.querySelectorAll('[data-action="close"]').forEach((button) => button.addEventListener('click', () => overlay.remove()));
    modal.querySelector('[data-action="prev"]')?.addEventListener('click', () => {
      wizardError = '';
      step -= 1;
      renderWizard();
    });

    modal.querySelector('[data-action="next"]')?.addEventListener('click', () => {
      wizardError = '';
      if (step === 1) {
        const value = (modal.querySelector('[name="stopDate"]') as HTMLInputElement).value;
        if (!value) {
          (modal.querySelector('[name="stopDate"]') as HTMLInputElement).reportValidity();
          return;
        }
        stopDate = value;
      } else if (step === 2) {
        const startValue = (modal.querySelector('[name="newStartDate"]') as HTMLInputElement).value;
        const rentValue = (modal.querySelector('[name="newRentAmount"]') as HTMLInputElement).value;
        if (!startValue) {
          (modal.querySelector('[name="newStartDate"]') as HTMLInputElement).reportValidity();
          return;
        }
        if (!rentValue) {
          (modal.querySelector('[name="newRentAmount"]') as HTMLInputElement).reportValidity();
          return;
        }
        newStartDate = startValue;
        newRentAmount = rentValue;
      }
      step += 1;
      renderWizard();
    });

    modal.querySelector('[data-action="confirm"]')?.addEventListener('click', async () => {
      const button = modal.querySelector('[data-action="confirm"]') as HTMLButtonElement;
      button.disabled = true;
      button.textContent = translate('common.processing');
      wizardError = '';
      try {
        await rentalAgreementsApi.update(agreement.id, {
          tenantId: tenant.id,
          propertyId: agreement.propertyId,
          rentAmount: agreement.rentAmount ?? 0,
          startDate: agreement.startDate ?? '',
          endDate: stopDate,
        });
        await rentalAgreementsApi.create({
          tenantId: tenant.id,
          propertyId: agreement.propertyId,
          rentAmount: parseFloat(newRentAmount),
          startDate: newStartDate,
        });
        overlay.remove();
        await onSaved();
      } catch (error) {
        console.error('Failed to apply rent change:', error);
        wizardError = error instanceof ApiError ? error.message : translate('agreement.rentChange.error');
        renderWizard();
      }
    });
  };

  overlay.appendChild(modal);
  overlay.addEventListener('click', (event) => { if (event.target === overlay) overlay.remove(); });
  document.body.appendChild(overlay);
  renderWizard();
}

export function openTimelineDrawer(options: {
  tenant: TenantDto;
  agreements: RentalAgreementDto[];
}): void {
  const { tenant, agreements } = options;
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';

  const modal = document.createElement('div');
  modal.className = 'modal modal-wide';

  const tenantAgreements = agreements
    .slice()
    .sort((left, right) => {
      const leftDate = left.startDate ? new Date(left.startDate).getTime() : 0;
      const rightDate = right.startDate ? new Date(right.startDate).getTime() : 0;
      return leftDate - rightDate;
    });

  const header = document.createElement('div');
  header.className = 'modal-header';
  header.innerHTML = `
    <div>
      <h2>${translate('tenantPage.timeline')}</h2>
      <p class="modal-subtitle">${escapeHtml(tenant.firstName)} ${escapeHtml(tenant.lastName)}</p>
    </div>
    <button class="modal-close" data-action="cancel">
      ${TENANT_ICONS.close}
    </button>
  `;
  modal.appendChild(header);

  const timeline = document.createElement('rc-timeline-events') as HTMLElement & { applyFilter?: () => void };
  timeline.setAttribute('tenant-id', tenant.id);
  timeline.setAttribute('tenant-name', `${tenant.firstName} ${tenant.lastName}`);

  if (tenantAgreements.length > 1) {
    const tabBar = document.createElement('div');
    tabBar.className = 'timeline-tabs';
    tabBar.innerHTML = `
      <button class="timeline-tab timeline-tab--active" data-tab-index="all">
        <span class="tab-label">${translate('tenant.timeline.allAgreements')}</span>
      </button>
      ${tenantAgreements.map((agreement, index) => `
        <button class="timeline-tab" data-tab-index="${index}">
          <span class="tab-address">${escapeHtml(agreement.propertyAddress)}</span>
          <span class="tab-period">${formatAgreementPeriod(agreement)}</span>
        </button>
      `).join('')}
    `;
    modal.appendChild(tabBar);

    tabBar.addEventListener('click', (event) => {
      const button = (event.target as HTMLElement).closest<HTMLElement>('[data-tab-index]');
      if (!button) return;

      tabBar.querySelectorAll('.timeline-tab').forEach((tab) => tab.classList.remove('timeline-tab--active'));
      button.classList.add('timeline-tab--active');

      const index = button.dataset.tabIndex;
      if (index === 'all') {
        timeline.removeAttribute('start-date');
        timeline.removeAttribute('end-date');
      } else {
        const agreement = tenantAgreements[parseInt(index!, 10)];
        if (agreement.startDate) timeline.setAttribute('start-date', agreement.startDate);
        else timeline.removeAttribute('start-date');
        if (agreement.endDate) timeline.setAttribute('end-date', agreement.endDate);
        else timeline.removeAttribute('end-date');
      }
      timeline.applyFilter?.();
    });
  }

  modal.appendChild(timeline);
  overlay.appendChild(modal);
  overlay.addEventListener('click', (event) => { if (event.target === overlay) overlay.remove(); });
  header.querySelector('[data-action="cancel"]')?.addEventListener('click', () => overlay.remove());
  document.body.appendChild(overlay);
}

export function openDocumentsDrawer(tenant: TenantDto): void {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';

  const modal = document.createElement('div');
  modal.className = 'modal modal-wide';
  const name = `${tenant.firstName} ${tenant.lastName}`;

  const render = async () => {
    let docs: DocumentDto[] = [];
    try {
      docs = await documentsApi.getByTenant(tenant.id);
    } catch {
      docs = [];
    }

    modal.innerHTML = `
      <div class="modal-header">
        <div>
          <h2>${translate('common.documents')}</h2>
          <p class="modal-subtitle">${escapeHtml(name)}</p>
        </div>
        <button class="modal-close" data-action="cancel">
          ${TENANT_ICONS.close}
        </button>
      </div>
      <div class="tab-toolbar">
        <button class="btn btn-primary btn-sm" data-action="upload">
          ${TENANT_ICONS.plusSm}
          ${translate('document.upload')}
        </button>
      </div>
      ${docs.length === 0
        ? `<div class="empty-state"><p>${translate('tenant.documents.empty')}</p></div>`
        : `<table>
            <thead><tr><th>${translate('common.file')}</th><th>${translate('common.type')}</th><th>${translate('common.expiryDate')}</th><th>${translate('common.actions')}</th></tr></thead>
            <tbody>
              ${docs.map((doc: any) => `
                <tr>
                  <td>${escapeHtml(doc.fileName)}</td>
                  <td><span class="badge badge-info">${getDocumentTypeLabel(doc.documentType)}</span></td>
                  <td>${doc.expiryDate ? doc.expiryDate : '-'}</td>
                  <td>
                    <div class="card-actions">
                      <button class="btn btn-outline btn-sm" data-action="download" data-id="${doc.id}">${translate('common.download')}</button>
                      <button class="btn btn-danger btn-sm" data-action="delete-doc" data-id="${doc.id}">${translate('common.delete')}</button>
                    </div>
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>`}
    `;

    modal.querySelector('[data-action="cancel"]')?.addEventListener('click', () => overlay.remove());
    modal.querySelector('[data-action="upload"]')?.addEventListener('click', () => openTenantDocForm(tenant, render));
    modal.querySelectorAll('[data-action="download"]').forEach((button) =>
      button.addEventListener('click', async () => {
        try {
          const documentId = (button as HTMLElement).dataset.id!;
          const document = docs.find((item) => item.id === documentId);
          await documentsApi.download(documentId, document?.fileName);
        } catch (error) {
          console.error(error);
        }
      }));
    modal.querySelectorAll('[data-action="delete-doc"]').forEach((button) =>
      button.addEventListener('click', async () => {
        if (!await confirmDialog(translate('document.confirmDelete'))) return;
        try {
          await documentsApi.delete((button as HTMLElement).dataset.id!);
          await render();
        } catch (error) {
          console.error(error);
        }
      }));
  };

  overlay.appendChild(modal);
  overlay.addEventListener('click', (event) => { if (event.target === overlay) overlay.remove(); });
  document.body.appendChild(overlay);
  void render();
}

export async function openTenantPortalInviteDialog(tenant: TenantDto, onSent: () => Promise<void>): Promise<void> {
  const overlay = appendModalOverlay(`
    <div class="modal">
      <div class="modal-header">
        <div>
          <h2>${translate('tenantPage.portalInviteDialogTitle')}</h2>
          <p class="modal-subtitle">${escapeHtml(tenant.firstName)} ${escapeHtml(tenant.lastName)}${tenant.email ? ` - ${escapeHtml(tenant.email)}` : ''}</p>
        </div>
        <button class="modal-close" data-action="cancel">
          ${TENANT_ICONS.close}
        </button>
      </div>
      <div class="candidate-convert-note">
        ${TENANT_ICONS.home}
        <span>${translate('tenantPage.portalInviteDialogFreeNote')}</span>
      </div>
      <form>
        <div class="form-error" data-form-error hidden></div>
        <div class="form-group">
          <label>${translate('tenantPage.portalInviteDialogMessageLabel')}</label>
          <textarea name="message" rows="5" placeholder="${translate('tenantPage.portalInviteDialogMessagePlaceholder')}"></textarea>
        </div>
        <div class="modal-actions">
          <button type="button" class="btn btn-outline" data-action="cancel">${translate('common.cancel')}</button>
          <button type="submit" class="btn btn-primary">${tenant.tenantPortalConnectionStatus === 'no-account-invite-sent' ? translate('tenantPage.portalInviteResendAction') : translate('tenantPage.portalInviteAction')}</button>
        </div>
      </form>
    </div>
  `, '200');

  const close = () => overlay.remove();
  overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
  overlay.querySelectorAll('[data-action="cancel"]').forEach((button) => button.addEventListener('click', close));

  overlay.querySelector('form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const submitButton = overlay.querySelector('[type="submit"]') as HTMLButtonElement;
    const errorBox = overlay.querySelector<HTMLElement>('[data-form-error]');
    const formData = new FormData(event.currentTarget as HTMLFormElement);
    submitButton.disabled = true;
    submitButton.textContent = translate('tenantPage.portalInviteSending');
    if (errorBox) {
      errorBox.hidden = true;
      errorBox.textContent = '';
    }

    try {
      const result = await tenantsApi.sendPortalInvite(tenant.id, (formData.get('message') as string) || undefined);
      close();
      await onSent();
      toastService.success(`${translate('tenantPage.portalInviteSuccess')} ${result.inviteUrl}`);
    } catch (error) {
      console.error('Failed to send tenant portal invite:', error);
      const message = error instanceof ApiError ? error.message : translate('tenantPage.portalInviteError');
      if (errorBox) {
        errorBox.hidden = false;
        errorBox.textContent = message;
      }
      submitButton.disabled = false;
      submitButton.textContent = tenant.tenantPortalConnectionStatus === 'no-account-invite-sent' ? translate('tenantPage.portalInviteResendAction') : translate('tenantPage.portalInviteAction');
    }
  });
}

function openTenantDocForm(tenant: TenantDto, onSuccess: () => void): void {
  const overlay = appendModalOverlay(`
    <div class="modal">
      <div class="modal-header">
        <h2>${translate('document.upload')}</h2>
        <button class="modal-close" data-action="cancel">
          ${TENANT_ICONS.close}
        </button>
      </div>
      <form>
        <div class="form-error" data-form-error hidden></div>
        <div class="form-group">
          <label>${translate('common.file')} *</label>
          <input name="file" type="file" accept=".pdf,.jpg,.jpeg,.png" required>
        </div>
        <div class="form-group">
          <label>${translate('common.type')} *</label>
          <select name="documentType" required>
            ${DOC_TYPES.map((type) => `<option value="${type}">${getDocumentTypeLabel(type)}</option>`).join('')}
          </select>
        </div>
        <div class="form-group">
          <label>${translate('common.expiryDate')}</label>
          <input name="expiryDate" type="date">
        </div>
        <div class="form-group">
          <label>${translate('common.tags')}</label>
          <input name="tags" placeholder="${translate('document.tagsPlaceholder')}">
        </div>
        <div class="modal-actions">
          <button type="button" class="btn btn-outline" data-action="cancel">${translate('common.cancel')}</button>
          <button type="submit" class="btn btn-primary">${translate('common.upload')}</button>
        </div>
      </form>
    </div>
  `, '200');

  const close = () => overlay.remove();
  overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
  overlay.querySelectorAll('[data-action="cancel"]').forEach((button) => button.addEventListener('click', close));

  overlay.querySelector('form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const submitBtn = overlay.querySelector('[type="submit"]') as HTMLButtonElement;
    const errorBox = overlay.querySelector<HTMLElement>('[data-form-error]');
    submitBtn.disabled = true;
    submitBtn.textContent = translate('common.uploading');
    if (errorBox) {
      errorBox.hidden = true;
      errorBox.textContent = '';
    }

    const formData = new FormData(event.target as HTMLFormElement);
    const file = formData.get('file') as File | null;
    if (!file) return;

    const data: CreateDocumentRequest = {
      tenantId: tenant.id,
      fileName: file.name,
      contentType: file.type,
      fileSizeBytes: file.size,
      documentType: formData.get('documentType') as string,
      expiryDate: (formData.get('expiryDate') as string) || undefined,
      tags: (formData.get('tags') as string) || undefined,
    };

    try {
      const result = await documentsApi.create(data);
      const uploadResponse = await fetch(result.uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
      if (!uploadResponse.ok) {
        await documentsApi.delete(result.documentId).catch(() => undefined);
        throw new Error(translate('document.uploadError'));
      }
      close();
      onSuccess();
    } catch (error) {
      console.error('Failed to upload:', error);
      if (errorBox) {
        errorBox.textContent = error instanceof ApiError && error.code === 'DOCUMENT_STORAGE_LIMIT_EXCEEDED'
          ? error.message
          : (error instanceof Error ? error.message : translate('document.uploadError'));
        errorBox.hidden = false;
      }
      submitBtn.disabled = false;
      submitBtn.textContent = translate('common.upload');
    }
  });
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function openTenantDossierExportModal(tenant: TenantDto): void {
  const overlay = appendModalOverlay(`
    <div class="modal">
      <div class="modal-header">
        <h2>${translate('dossier.tenant.modalTitle')}</h2>
        <button class="modal-close" data-action="cancel">
          ${TENANT_ICONS.close}
        </button>
      </div>
      <p class="modal-subtitle">${escapeHtml(`${tenant.firstName} ${tenant.lastName}`)}</p>
      <p class="modal-subtitle">${translate('dossier.tenant.modalDescription')}</p>
      <form>
        <div class="form-error" data-form-error hidden></div>
        <div class="dossier-options">
          <label class="dossier-option">
            <input type="checkbox" name="includeContactDetails" checked>
            <span class="dossier-option-copy">${translate('dossier.tenant.section.contact')}</span>
          </label>
          <label class="dossier-option">
            <input type="checkbox" name="includeRentalAgreements" checked>
            <span class="dossier-option-copy">${translate('dossier.tenant.section.agreements')}</span>
          </label>
          <label class="dossier-option">
            <input type="checkbox" name="includeDocuments" checked>
            <span class="dossier-option-copy">${translate('dossier.tenant.section.documents')}</span>
          </label>
          <label class="dossier-option">
            <input type="checkbox" name="includeTimeline" checked>
            <span class="dossier-option-copy">${translate('dossier.tenant.section.timeline')}</span>
          </label>
          <label class="dossier-option">
            <input type="checkbox" name="includeRelatedReminders" checked>
            <span class="dossier-option-copy">${translate('dossier.tenant.section.reminders')}</span>
          </label>
        </div>
        <div class="modal-actions">
          <button type="button" class="btn btn-outline" data-action="cancel">${translate('common.cancel')}</button>
          <button type="submit" class="btn btn-primary">${translate('common.export')}</button>
        </div>
      </form>
    </div>
  `);

  const close = () => overlay.remove();
  overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
  overlay.querySelectorAll('[data-action="cancel"]').forEach((button) => button.addEventListener('click', close));

  overlay.querySelector('form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    const submitBtn = form.querySelector('[type="submit"]') as HTMLButtonElement;
    const errorBox = form.querySelector<HTMLElement>('[data-form-error]');
    const data: ExportTenantDossierRequest = {
      includeContactDetails: (form.elements.namedItem('includeContactDetails') as HTMLInputElement).checked,
      includeRentalAgreements: (form.elements.namedItem('includeRentalAgreements') as HTMLInputElement).checked,
      includeDocuments: (form.elements.namedItem('includeDocuments') as HTMLInputElement).checked,
      includeTimeline: (form.elements.namedItem('includeTimeline') as HTMLInputElement).checked,
      includeRelatedReminders: (form.elements.namedItem('includeRelatedReminders') as HTMLInputElement).checked,
    };

    if (errorBox) {
      errorBox.hidden = true;
      errorBox.textContent = '';
    }
    if (!Object.values(data).some(Boolean)) {
      if (errorBox) {
        errorBox.hidden = false;
        errorBox.textContent = translate('dossier.error.emptySelection');
      }
      return;
    }

    submitBtn.disabled = true;
    submitBtn.textContent = translate('common.generating');

    try {
      const result = await dossiersApi.exportTenant(tenant.id, data);
      downloadBlob(result.blob, result.fileName);
      close();
    } catch (error) {
      console.error('Failed to export tenant dossier:', error);
      submitBtn.disabled = false;
      submitBtn.textContent = translate('common.export');
      showDialogError(form, error instanceof ApiError ? error.message : translate('dossier.error.exportFailed'));
    }
  });
}
